import { ServiceLogo } from "@/components/ServiceLogo";
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ProBadge from "@/components/ProBadge";
import NeighborMap from "@/components/NeighborMap";
import LikeButton from "@/components/LikeButton";
import ShareButton from "@/components/ShareButton";
import { HandHeart, MapPin, MessageSquare, Plus, HelpingHand, Search, ShieldAlert, Info, List, Map as MapIcon, ImagePlus, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

const KIND_META = {
  request: { label: "Je cherche", cls: "bg-orange-100 text-orange-700", icon: Search },
  offer: { label: "Je propose", cls: "bg-emerald-100 text-emerald-700", icon: HelpingHand },
};

function PostCard({ p }) {
  const km = KIND_META[p.kind] || KIND_META.request;
  const KindIcon = km.icon;
  return (
    <Link to={`/entre-voisins/${p.id}`} data-testid={`voisin-card-${p.id}`}
      className="block bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-lg hover:border-[#E05638]/40 transition-all">
      {(p.photos?.[0] || p.photo) && (
        <img src={p.photos?.[0] || p.photo} alt={p.title} data-testid={`voisin-card-img-${p.id}`}
          className="mb-3 -mx-5 -mt-5 h-40 w-[calc(100%+2.5rem)] object-cover rounded-t-2xl" />
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${km.cls}`}><KindIcon size={12} /> {km.label}</span>
        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{p.category}</span>
        {p.demo && <span data-testid="demo-ribbon" className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-200 px-2 py-0.5 rounded-full">Démo</span>}
        {p.is_pro && <ProBadge size="sm" testId={`voisin-card-pro-${p.id}`} />}
        {p.resolved && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">✓ Résolu</span>}
      </div>
      <h3 className="mt-2 font-heading font-bold text-slate-900 line-clamp-2">{p.title}</h3>
      <p className="mt-1 text-sm text-slate-600 line-clamp-2">{p.description}</p>
      <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        <span className="font-semibold text-slate-700">{p.author_name}</span>
        {p.city && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {p.city}</span>}
        <span>{timeAgo(p.created_at)}</span>
        <span className="inline-flex items-center gap-1 text-[#E05638] font-semibold"><MessageSquare size={12} /> {p.replies_count || 0}</span>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
        <LikeButton type="voisin" id={p.id} liked={p.liked} count={p.likes} variant="icon" testId={`voisin-card-like-${p.id}`} />
        <ShareButton url={`${process.env.REACT_APP_BACKEND_URL}/api/voisin-vitrine/${p.id}`} title={p.title} text={`${p.title} — Entre Voisins · ${p.city || "Eure-et-Loir"}`} variant="icon" testId={`voisin-card-share-${p.id}`} />
      </div>
    </Link>
  );
}

export default function EntreVoisins() {
  useSeo({
    title: "Entre voisins — entraide et petites annonces locales en Eure-et-Loir | PROXIreduc-28",
    description: "Échangez, partagez et entraidez-vous entre voisins en Eure-et-Loir. Services, prêts, dons et bons plans entre habitants de Chartres, Dreux et du département 28.",
    canonicalPath: "/entre-voisins",
    image: "/og-image.jpg",
  });
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], cities: [], categories: [] });
  const [kind, setKind] = useState("");
  const [category, setCategory] = useState("");
  const [city, setCity] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [near, setNear] = useState(false);
  const [form, setForm] = useState({ kind: "request", title: "", description: "", category: "", city: "", postal_code: "", contact: "", photos: [], name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const fileRef = useRef(null);

  const reorderPhotos = (from, to) => {
    if (from == null || to == null || from === to) return;
    setForm((f) => {
      const arr = [...f.photos];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...f, photos: arr };
    });
  };

  const onPickPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const room = 5 - form.photos.length;
    if (room <= 0) { toast.error("5 photos maximum."); return; }
    if (files.length > room) toast.error("5 photos maximum.");
    setUploading(true);
    try {
      for (const file of files.slice(0, room)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/entre-voisins/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => (f.photos.length < 5 ? { ...f, photos: [...f.photos, data.url] } : f));
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const load = () => {
    const params = new URLSearchParams();
    if (kind) params.set("kind", kind);
    if (category) params.set("category", category);
    if (city) params.set("city", city);
    if (near && user?.city) params.set("near", user.city);
    api.get(`/entre-voisins?${params.toString()}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, [kind, category, city, near]); // eslint-disable-line

  const submit = async () => {
    if (!form.title || form.title.trim().length < 4) { toast.error("Titre trop court."); return; }
    if (!form.description || form.description.trim().length < 10) { toast.error("Décrivez un peu plus votre demande."); return; }
    if (!form.category) { toast.error("Choisissez une catégorie."); return; }
    if (!form.city.trim()) { toast.error("Indiquez votre ville."); return; }
    const anon = !(user && user !== false);
    if (anon && (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim()))) { toast.error("Ajoutez votre e-mail pour recevoir les réponses."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/entre-voisins", form);
      toast.success(data.account_created ? "Annonce en ligne ! Un compte membre a été créé — vos identifiants vous ont été envoyés par e-mail." : "Publication en ligne !");
      setOpen(false);
      setForm({ kind: "request", title: "", description: "", category: "", city: "", postal_code: "", contact: "", photos: [], name: "", email: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const cats = data.categories || [];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="entre-voisins-page">
      {/* En-tête */}
      <div className="rounded-3xl bg-gradient-to-br from-[#E05638] to-[#f08a5d] text-white p-7 sm:p-10 relative overflow-hidden">
        <div className="relative z-10">
          <ServiceLogo serviceKey="entre-voisins" size={76} className="mb-4" />
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"><HandHeart size={14} /> Entraide locale · 100% gratuit</div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold">Entre Voisins</h1>
          <p className="mt-2 text-white/90 max-w-2xl text-sm sm:text-base">Un coup de main à donner ou à recevoir en Eure-et-Loir ? Habitants et pros s'entraident ici, gratuitement et près de chez vous.</p>
          <div className="mt-5">
            {(
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#E05638] hover:bg-orange-50 font-bold" data-testid="voisin-new-btn"><Plus size={16} className="mr-1.5" /> Poster une annonce</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="voisin-new-dialog">
                  <DialogHeader><DialogTitle>Nouvelle annonce d'entraide</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => setForm({ ...form, kind: "request" })} className={`p-3 rounded-xl border text-sm font-semibold ${form.kind === "request" ? "border-[#E05638] bg-orange-50 text-[#E05638]" : "border-slate-200 text-slate-600"}`} data-testid="voisin-kind-request">🙋 Je cherche de l'aide</button>
                      <button onClick={() => setForm({ ...form, kind: "offer" })} className={`p-3 rounded-xl border text-sm font-semibold ${form.kind === "offer" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600"}`} data-testid="voisin-kind-offer">🤝 Je propose mon aide</button>
                    </div>
                    <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Besoin d'aide pour monter un meuble" data-testid="voisin-title" /></div>
                    <div><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez votre besoin ou ce que vous proposez…" data-testid="voisin-desc" /></div>
                    <div><Label>Catégorie</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="voisin-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                        <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>Ville</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Chartres" data-testid="voisin-city" /></div>
                      <div><Label>Code postal</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="28000" data-testid="voisin-postal" /></div>
                    </div>
                    <div><Label>Téléphone / contact public (optionnel)</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Tél. à afficher sur l'annonce (facultatif)" data-testid="voisin-contact" /></div>
                    {!(user && user !== false) && (
                      <div className="grid grid-cols-2 gap-2" data-testid="voisin-anon-fields">
                        <div><Label>Prénom <span className="text-slate-400 font-normal">(facultatif)</span></Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Votre prénom" data-testid="voisin-anon-name" /></div>
                        <div><Label>Votre e-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="vous@email.fr" data-testid="voisin-anon-email" /></div>
                        <p className="col-span-2 -mt-1 text-[11px] text-slate-400">Pour recevoir les réponses à votre annonce.</p>
                      </div>
                    )}
                    <div>
                      <Label>Photos <span className="font-normal text-slate-500">({form.photos.length}/5)</span> — la 1<sup>re</sup> est l'image principale</Label>
                      {form.photos.length > 1 && (
                        <p className="mt-0.5 text-[11px] text-slate-400" data-testid="voisin-photo-reorder-hint">Glissez-déposez les vignettes pour changer l'ordre.</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {form.photos.map((u, i) => (
                          <div
                            key={u}
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { reorderPhotos(dragIdx, i); setDragIdx(null); }}
                            onDragEnd={() => setDragIdx(null)}
                            className={`relative w-20 h-20 rounded-lg overflow-hidden border cursor-move transition-opacity ${dragIdx === i ? "opacity-40 border-[#E05638]" : "border-slate-200"}`}
                            data-testid={`voisin-photo-item-${i}`}
                          >
                            <img src={u} alt="" className="w-full h-full object-cover pointer-events-none" />
                            {i === 0 && <span className="absolute top-0 left-0 bg-[#E05638] text-white text-[9px] font-bold px-1 rounded-br">Principale</span>}
                            <button type="button" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== u) }))} className="absolute top-0 right-0 bg-black/60 text-white p-0.5 rounded-bl" data-testid={`voisin-photo-remove-${i}`}><X size={12} /></button>
                            {form.photos.length > 1 && (
                              <div className="absolute bottom-0 inset-x-0 flex justify-center gap-2 bg-black/45 py-0.5">
                                <button type="button" disabled={i === 0} onClick={() => reorderPhotos(i, i - 1)} className="text-white disabled:opacity-25" aria-label="Déplacer avant" data-testid={`voisin-photo-left-${i}`}><ChevronLeft size={14} /></button>
                                <button type="button" disabled={i === form.photos.length - 1} onClick={() => reorderPhotos(i, i + 1)} className="text-white disabled:opacity-25" aria-label="Déplacer après" data-testid={`voisin-photo-right-${i}`}><ChevronRight size={14} /></button>
                              </div>
                            )}
                          </div>
                        ))}
                        {form.photos.length < 5 && (
                          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#E05638] hover:text-[#E05638] grid place-items-center transition-colors" data-testid="voisin-photo-add">
                            {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={20} />}
                          </button>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickPhotos} data-testid="voisin-photo-input" />
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> Service gratuit d'entraide. Les échanges se font sous votre responsabilité ; ne partagez pas d'informations sensibles.</p>
                    <Button onClick={submit} disabled={saving} className="w-full bg-[#E05638] hover:bg-[#c94a2f]" data-testid="voisin-submit">{saving ? "Publication…" : "Publier"}</Button>
                    {!(user && user !== false) && (
                      <p className="text-[11px] text-slate-400 text-center" data-testid="voisin-account-note">Un espace membre gratuit est créé pour suivre vos réponses — accès envoyé par e-mail.</p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Avertissement */}
      <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="voisin-disclaimer">
        <ShieldAlert size={15} className="text-amber-600 mt-0.5 shrink-0" />
        <span>PROXIreduc-28 met en relation des voisins à titre gratuit et n'intervient pas dans les échanges. Restez vigilant, privilégiez les lieux publics et signalez tout abus.</span>
      </div>

      {/* Filtres */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          {[["", "Tout"], ["request", "Je cherche"], ["offer", "Je propose"]].map(([v, l]) => (
            <button key={v} onClick={() => setKind(v)} data-testid={`voisin-filter-kind-${v || "all"}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border transition-colors ${kind === v ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[220px]" data-testid="voisin-filter-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes catégories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={city || "all"} onValueChange={(v) => setCity(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[180px]" data-testid="voisin-filter-city"><SelectValue placeholder="Ville" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toutes les villes</SelectItem>{(data.cities || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-2">
          {user && user !== false && user.city && (
            <button onClick={() => setNear((v) => !v)} data-testid="voisin-near-me"
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold border inline-flex items-center gap-1.5 transition-colors ${near ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white text-slate-600 border-slate-200 hover:border-[#E05638]"}`}>
              <MapPin size={14} /> Près de moi{near && user.city ? ` · ${user.city}` : ""}
            </button>
          )}
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
            <button onClick={() => setView("list")} className={`px-3 py-1 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 ${view === "list" ? "bg-white shadow text-[#E05638]" : "text-slate-500"}`} data-testid="voisin-view-list"><List size={14} /> Liste</button>
            <button onClick={() => setView("map")} className={`px-3 py-1 rounded-md text-sm font-semibold inline-flex items-center gap-1.5 ${view === "map" ? "bg-white shadow text-[#E05638]" : "text-slate-500"}`} data-testid="voisin-view-map"><MapIcon size={14} /> Carte</button>
          </div>
        </div>
      </div>

      {near && user?.city && <p className="mt-2 text-xs text-slate-500" data-testid="voisin-near-hint">Trié par proximité de <strong>{user.city}</strong>.</p>}

      {/* Liste ou Carte */}
      {view === "map" ? (
        <div className="mt-6" data-testid="voisin-map-wrap"><NeighborMap posts={data.items} center={near ? data.center : null} /></div>
      ) : (
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="voisin-list">
        {data.items.length === 0 ? (
          <div className="col-span-full text-center text-slate-400 py-16">Aucune annonce pour l'instant. Soyez le premier à proposer ou demander un coup de main !</div>
        ) : data.items.map((p) => <PostCard key={p.id} p={p} />)}
      </div>
      )}
    </div>
  );
}
